// Main 4D Tetris Game Logic

import { Grid4D, GridConfig } from './Grid4D';
import {
  Tetromino4D,
  Block4D,
  getRandomTetromino,
  rotateXY,
  rotateXZ,
  rotateXW,
  normalizeBlocks,
} from './Tetromino4D';

export type GameState = 'playing' | 'paused' | 'gameover';

export interface ActivePiece {
  tetromino: Tetromino4D;
  position: { x: number; y: number; z: number; w: number };
}

export interface GameEvents {
  onScoreChange: (score: number) => void;
  onLevelChange: (level: number) => void;
  onLinesCleared: (lines: number) => void;
  onGameOver: () => void;
  onPieceSpawn: (piece: ActivePiece) => void;
  onPieceLock: () => void;
  onDimensionChange: (w: number) => void;
}

export class Game4D {
  public grid: Grid4D;
  public activePiece: ActivePiece | null = null;
  public nextPiece: Tetromino4D;
  public currentW: number = 1; // Current dimension being viewed
  public state: GameState = 'playing';
  public score: number = 0;
  public level: number = 1;
  public linesCleared: number = 0;

  private dropInterval: number = 1000; // ms between automatic drops
  private lastDropTime: number = 0;
  private events: GameEvents;
  private config: GridConfig;

  constructor(events: GameEvents) {
    this.config = {
      sizeX: 4, // 4 wide
      sizeY: 12, // 12 tall
      sizeZ: 4, // 4 deep
      sizeW: 3, // 3 parallel dimensions
    };

    this.grid = new Grid4D(this.config);
    this.events = events;
    this.nextPiece = getRandomTetromino();
    // Don't spawn piece here - let App call it after initialization
  }

  // Spawn a new piece at the top
  spawnPiece(): boolean {
    const tetromino = this.nextPiece;
    this.nextPiece = getRandomTetromino();

    // Start in the middle of the grid
    const startX = Math.floor((this.config.sizeX - 2) / 2);
    const startZ = Math.floor((this.config.sizeZ - 2) / 2);
    const startW = Math.floor(this.config.sizeW / 2);

    this.activePiece = {
      tetromino,
      position: { x: startX, y: 0, z: startZ, w: startW },
    };

    // Check if the piece can be placed
    if (!this.isValidPosition()) {
      this.state = 'gameover';
      this.events.onGameOver();
      return false;
    }

    this.events.onPieceSpawn(this.activePiece);
    return true;
  }

  // Get absolute positions of all blocks in the active piece
  getAbsoluteBlocks(): Block4D[] {
    if (!this.activePiece) return [];

    const { position, tetromino } = this.activePiece;
    return tetromino.blocks.map(block => ({
      x: block.x + position.x,
      y: block.y + position.y,
      z: block.z + position.z,
      w: block.w + position.w,
    }));
  }

  // Check if current piece position is valid
  isValidPosition(): boolean {
    if (!this.activePiece) return false;

    const blocks = this.getAbsoluteBlocks();
    for (const block of blocks) {
      // Check bounds
      if (!this.grid.isInBounds(block.x, block.y, block.z, block.w)) {
        return false;
      }
      // Check collision with existing blocks
      if (!this.grid.isEmpty(block.x, block.y, block.z, block.w)) {
        return false;
      }
    }
    return true;
  }

  // Move piece in a direction
  move(dx: number, dy: number, dz: number, dw: number): boolean {
    if (!this.activePiece || this.state !== 'playing') return false;

    const oldPosition = { ...this.activePiece.position };
    this.activePiece.position.x += dx;
    this.activePiece.position.y += dy;
    this.activePiece.position.z += dz;
    this.activePiece.position.w += dw;

    if (!this.isValidPosition()) {
      this.activePiece.position = oldPosition;
      return false;
    }

    return true;
  }

  // Move in X direction
  moveX(direction: number): boolean {
    return this.move(direction, 0, 0, 0);
  }

  // Move in Z direction (depth)
  moveZ(direction: number): boolean {
    return this.move(0, 0, direction, 0);
  }

  // Move in W direction (4th dimension)
  moveW(direction: number): boolean {
    return this.move(0, 0, 0, direction);
  }

  // Rotate piece
  rotate(rotationType: 'xy' | 'xz' | 'xw'): boolean {
    if (!this.activePiece || this.state !== 'playing') return false;

    const oldBlocks = this.activePiece.tetromino.blocks.map(b => ({ ...b }));

    let newBlocks: Block4D[];
    switch (rotationType) {
      case 'xy':
        newBlocks = rotateXY(this.activePiece.tetromino.blocks);
        break;
      case 'xz':
        newBlocks = rotateXZ(this.activePiece.tetromino.blocks);
        break;
      case 'xw':
        newBlocks = rotateXW(this.activePiece.tetromino.blocks);
        break;
    }

    // Normalize to keep piece origin consistent
    this.activePiece.tetromino.blocks = normalizeBlocks(newBlocks);

    if (!this.isValidPosition()) {
      // Try wall kicks
      const kicks = [
        { x: 1, y: 0, z: 0, w: 0 },
        { x: -1, y: 0, z: 0, w: 0 },
        { x: 0, y: 0, z: 1, w: 0 },
        { x: 0, y: 0, z: -1, w: 0 },
        { x: 0, y: 0, z: 0, w: 1 },
        { x: 0, y: 0, z: 0, w: -1 },
        { x: 0, y: -1, z: 0, w: 0 },
      ];

      let kicked = false;
      for (const kick of kicks) {
        this.activePiece.position.x += kick.x;
        this.activePiece.position.y += kick.y;
        this.activePiece.position.z += kick.z;
        this.activePiece.position.w += kick.w;

        if (this.isValidPosition()) {
          kicked = true;
          break;
        }

        this.activePiece.position.x -= kick.x;
        this.activePiece.position.y -= kick.y;
        this.activePiece.position.z -= kick.z;
        this.activePiece.position.w -= kick.w;
      }

      if (!kicked) {
        this.activePiece.tetromino.blocks = oldBlocks;
        return false;
      }
    }

    return true;
  }

  // Soft drop (one cell down)
  softDrop(): boolean {
    return this.move(0, 1, 0, 0);
  }

  // Hard drop (instant drop to bottom)
  hardDrop(): void {
    if (!this.activePiece || this.state !== 'playing') return;

    let dropDistance = 0;
    while (this.move(0, 1, 0, 0)) {
      dropDistance++;
    }

    // Bonus points for hard drop
    this.score += dropDistance * 2;
    this.events.onScoreChange(this.score);

    this.lockPiece();
  }

  // Lock the current piece in place
  lockPiece(): void {
    if (!this.activePiece) return;

    const blocks = this.getAbsoluteBlocks();
    const color = this.activePiece.tetromino.color;

    for (const block of blocks) {
      this.grid.setCell(block.x, block.y, block.z, block.w, true, color);
    }

    this.events.onPieceLock();

    // Clear lines and score
    const cleared = this.grid.clearCompleteLinesPerDimension();
    if (cleared > 0) {
      this.linesCleared += cleared;
      this.events.onLinesCleared(cleared);

      // Score based on lines cleared (4D bonus multiplier)
      const baseScore = [0, 100, 300, 500, 800, 1200, 1600, 2000];
      const scoreIndex = Math.min(cleared, baseScore.length - 1);
      this.score += baseScore[scoreIndex] * this.level;
      this.events.onScoreChange(this.score);

      // Level up every 10 lines
      const newLevel = Math.floor(this.linesCleared / 10) + 1;
      if (newLevel > this.level) {
        this.level = newLevel;
        this.dropInterval = Math.max(100, 1000 - (this.level - 1) * 100);
        this.events.onLevelChange(this.level);
      }
    }

    // Spawn next piece
    this.spawnPiece();
  }

  // Update game state (called every frame)
  update(currentTime: number): void {
    if (this.state !== 'playing') return;

    // Automatic drop
    if (currentTime - this.lastDropTime >= this.dropInterval) {
      if (!this.softDrop()) {
        // Piece can't move down, lock it
        this.lockPiece();
      }
      this.lastDropTime = currentTime;
    }
  }

  // Toggle pause
  togglePause(): void {
    if (this.state === 'playing') {
      this.state = 'paused';
    } else if (this.state === 'paused') {
      this.state = 'playing';
    }
  }

  // Change viewed dimension
  setViewDimension(w: number): void {
    if (w >= 0 && w < this.config.sizeW) {
      this.currentW = w;
      this.events.onDimensionChange(w);
    }
  }

  // Restart the game
  restart(): void {
    this.grid.reset();
    this.score = 0;
    this.level = 1;
    this.linesCleared = 0;
    this.dropInterval = 1000;
    this.state = 'playing';
    this.currentW = 1;
    this.nextPiece = getRandomTetromino();

    this.events.onScoreChange(this.score);
    this.events.onLevelChange(this.level);
    this.events.onDimensionChange(this.currentW);

    this.spawnPiece();
  }

  // Get blocks to render for current view
  getVisibleBlocks(): {
    gridBlocks: { x: number; y: number; z: number; w: number; color: number }[];
    activeBlocks: { x: number; y: number; z: number; w: number; color: number }[];
    ghostBlocks: { x: number; y: number; z: number; w: number; color: number }[];
  } {
    const gridBlocks = this.grid.getFilledCells();

    const activeBlocks: { x: number; y: number; z: number; w: number; color: number }[] = [];
    const ghostBlocks: { x: number; y: number; z: number; w: number; color: number }[] = [];

    if (this.activePiece && this.state !== 'gameover') {
      const color = this.activePiece.tetromino.color;

      // Active piece blocks
      for (const block of this.getAbsoluteBlocks()) {
        activeBlocks.push({ ...block, color });
      }

      // Ghost piece (preview of where it will land)
      const originalY = this.activePiece.position.y;
      while (this.move(0, 1, 0, 0)) {
        // Keep moving down
      }

      for (const block of this.getAbsoluteBlocks()) {
        ghostBlocks.push({ ...block, color });
      }

      // Restore position
      this.activePiece.position.y = originalY;
    }

    return { gridBlocks, activeBlocks, ghostBlocks };
  }
}
